import type { Event } from '@buzzmap/shared';
import { categoryHexColors, categoryLabels, formatDateRange } from '../../utils';
import { EventLink } from '../events/EventLink';
import { EventCoverImage } from './EventCoverImage';
import styles from './EventFeatCard.module.css';

interface EventFeatCardProps {
  event: Event;
  onClick: () => void;
  /**
   * T105-FE: candidato LCP della vista iniziale (eager + fetchpriority high). Default false:
   * lo imposta solo chi sa che la carta è visibile senza scorrere.
   */
  priority?: boolean;
}

/** La carta ha larghezza fissa di 280 px (EventFeatCard.module.css). */
const COVER_SIZES = '280px';

/**
 * Vertical 4:5 featured card used in desktop home carousels.
 * Image chain: sponsor → scraped (https only) → branded category fallback (/fallbacks/*.jpg).
 * T105-FE: la foto è un <img> (prima doppio background-image, che scaricava foto e ripiego insieme):
 * il ripiego si carica solo se la foto fallisce e le carte fuori schermo sono lazy.
 *
 * R17: this used to be a <button>, and a link inside a button is not valid
 * HTML — the card is now a plain (non-interactive) container and the
 * crawlable link lives on the title, extended over the whole card through
 * its ::after, carrying the same onClick action.
 */
export function EventFeatCard({ event, onClick, priority = false }: EventFeatCardProps) {
  const catColor = categoryHexColors[event.category] ?? categoryHexColors.altro!;
  const label = categoryLabels[event.category] ?? 'Evento';

  const style: React.CSSProperties = {
    ['--cat-color' as string]: catColor,
  };

  return (
    <div className={`${styles.card} ${styles.withImage}`} style={style}>
      <EventCoverImage
        event={event}
        className={styles.photo}
        sizes={COVER_SIZES}
        priority={priority}
      />
      <span className={styles.overlay} aria-hidden="true" />
      <span className={styles.body}>
        <span className={styles.catTag} style={{ color: catColor }}>
          {label}
        </span>
        <span className={styles.title}>
          <EventLink eventId={event.id} onClick={onClick} extended>
            {event.title}
          </EventLink>
        </span>
        <span className={styles.meta}>
          <span>{formatDateRange(event.startDate, event.endDate)}</span>
          {event.location?.city && (
            <>
              <span aria-hidden="true">•</span>
              <span>{event.location.city}</span>
            </>
          )}
        </span>
      </span>
    </div>
  );
}
